import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, ScoreHeader, ScoreMetric } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';
import { getParameterImportance, IMPORTANCE_META } from '../tokens/riskFramework';

const fmt1 = (n: number | null | undefined): string => (n != null && Number.isFinite(n) ? n.toFixed(1) : '—');

// API returns scores on a 0–4 scale; we present them on a 0–10 scale where
// LOWER values mean a healthier credit profile (lower risk). Direction matches
// the API — no inversion needed.
const scaleToTen = (score4: number | null | undefined): number | null =>
    score4 == null || !Number.isFinite(score4) ? null : (score4 / 4) * 10;

// Grade buckets on the 0–10 scale. Low = best (A+), high = worst (D).
const gradeFromScore = (n: number | null | undefined): string => {
    if (n == null || !Number.isFinite(n)) return '—';
    if (n <= 1.5) return 'A+';
    if (n <= 3) return 'A';
    if (n <= 4.5) return 'B+';
    if (n <= 6) return 'B';
    if (n <= 8) return 'C';
    return 'D';
};

const toneForScore = (s: number | null): Severity => {
    if (s == null || !Number.isFinite(s)) return 'MEDIUM';
    if (s <= 3.5) return 'LOW';
    if (s <= 5) return 'MEDIUM';
    if (s <= 7) return 'HIGH';
    return 'SEVERE';
};

const toneLabel = (tone: Severity): string => {
    if (tone === 'LOW') return 'Low Risk';
    if (tone === 'MEDIUM') return 'Medium Risk';
    if (tone === 'HIGH') return 'Elevated Risk';
    return 'Severe Risk';
};

// ── Local severity palette (Score module only) ──────────────────────────
//
// Figma 131:85392 uses softer card surfaces than the shared `severity` map
// (which is tuned for inline chips / status pills, not large card fills).
// Per skill rule 7 the shared `severity` palette stays untouched — other
// modules (Alerts, Compliances, status chips) depend on its current values.
// These overrides are scoped to the Score module's cards + banner.
const SCORE_TONE: Record<Severity, { bg: string; border: string; ink: string; subdued: string }> = {
    LOW: { bg: '#D1FAE5', border: '#A7F3D0', ink: '#059669', subdued: '#047857' },
    MEDIUM: { bg: '#FFF7ED', border: '#FED7AA', ink: '#D97706', subdued: '#B45309' },
    HIGH: { bg: '#FEF2F2', border: '#FECACA', ink: '#DC2626', subdued: '#B91C1C' },
    SEVERE: { bg: '#FEE2E2', border: '#FCA5A5', ink: '#991B1B', subdued: '#7F1D1D' },
};

// Composite banner copy per tone — paired with the tone label as a single
// inline kicker line ("MEDIUM RISK · score 3.5–5. Monitor with…").
const TONE_BANNER_COPY: Record<Severity, string> = {
    LOW: 'score ≤ 3.5. Healthy zone — no immediate credit action.',
    MEDIUM: 'score 3.5–5. Monitor with standard due-diligence cadence.',
    HIGH: 'score 5–7. Material concerns — deeper diligence warranted.',
    SEVERE: 'score > 7. Severe risk — treat as red flag for credit decisioning.',
};

const scoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore != null);
const unscoredMetrics = (h: ScoreHeader): ScoreMetric[] => h.metrics.filter((m) => m.metricScore == null);

const PILLAR_FALLBACK_DESCRIPTION: Record<string, string> = {
    'Revenue Score': 'Top-line strength — absolute scale of revenue and recent growth trajectory.',
    'Stability Score': 'Volatility in revenue and cash inflows across recent quarters.',
    'Customer Concentration Score': 'Dependence on the largest customers — risk if a few accounts dominate revenue.',
    'Counter Party Stability': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Counter Party Stability Score': 'Health of the company’s top customers and vendors based on their own credit signals.',
    'Liquidity Score': 'Short-term ability to meet obligations from current assets and cash on hand.',
    'Profitability Score': 'Operating margins and net profitability over the most recent reported period.',
    'Working Capital Score': 'Trade receivable / payable cycle and inventory holding efficiency.',
    'Compliance Score': 'Punctuality of statutory filings — GST returns, EPF and tax compliance.',
    'Solvency Score': 'Long-term debt servicing capacity relative to equity and cashflows.',
    'Leverage Score': 'Total debt and gearing relative to net worth and earnings.',
};

const pillarDescription = (h: ScoreHeader): string | undefined => h.tooltipDescription || PILLAR_FALLBACK_DESCRIPTION[h.displayName];

const COMPOSITE_DESCRIPTION =
    'AICA Risk Score is a composite credit grade on a 0–10 scale derived from filings, bureau data and behavioural signals. Lower = stronger credit profile / lower risk.';

const isMetricRedundantWithHeader = (h: ScoreHeader): boolean => {
    const scored = scoredMetrics(h);
    if (scored.length !== 1) return false;
    const m = scored[0];
    return (
        m.displayName.trim().toLowerCase() === h.displayName.trim().toLowerCase() &&
        h.headerScore != null &&
        Math.abs((m.metricScore ?? -999) - h.headerScore) < 0.05
    );
};

const weakestDrivers = (h: ScoreHeader, n = 3): ScoreMetric[] => {
    if (isMetricRedundantWithHeader(h)) return [];
    return scoredMetrics(h)
        .slice()
        .sort((a, b) => (b.metricScore ?? 0) - (a.metricScore ?? 0))
        .slice(0, n);
};

// Per-driver tone — colour the score on the right of each driver row based on
// that driver's own score, not the parent pillar's tone. Matches Figma: within
// the Financial card, "Debt / Equity 4.0" reads as green even though the
// pillar overall is amber.
const driverToneInk = (s: number | null): string => SCORE_TONE[toneForScore(s)].ink;

// ---------------- PDF ----------------

const PdfPillarCard: React.FC<{ header: ScoreHeader }> = ({ header }) => {
    const scaledHeader = scaleToTen(header.headerScore);
    const tone = toneForScore(scaledHeader);
    const sev = SCORE_TONE[tone];
    const drivers = weakestDrivers(header);
    const description = pillarDescription(header);
    return (
        <View
            style={{
                paddingHorizontal: 11,
                paddingTop: 10,
                paddingBottom: 12,
                borderRadius: 4,
                backgroundColor: sev.bg,
                borderWidth: 0.5,
                borderColor: sev.border,
            }}
        >
            {/* Header: title (left) + score / 10 (right) */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text
                    style={{
                        flex: 1,
                        fontSize: 9,
                        fontFamily: fontFamily.body,
                        fontWeight: 600,
                        color: colors.ink00,
                        lineHeight: 1.3,
                        marginRight: 6,
                    }}
                >
                    {header.displayName}
                </Text>
                {/* Bottom-align "/ 10" with the bottom of the big score digit
                    (react-pdf baseline alignment is unreliable — flex-end +
                    matching lineHeight: 1 on both Texts gives a clean visual
                    bottom alignment). */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', flexShrink: 0 }}>
                    <Text
                        style={{
                            fontSize: 14,
                            fontFamily: fontFamily.mono,
                            fontWeight: 600,
                            color: colors.ink00,
                            lineHeight: 1,
                        }}
                    >
                        {fmt1(scaledHeader)}
                    </Text>
                    <Text style={{ fontSize: 7.5, color: colors.ink40, marginLeft: 3, fontFamily: fontFamily.mono, lineHeight: 1 }}>
                        / 10
                    </Text>
                </View>
            </View>
            {/* Tone label (e.g. "MEDIUM RISK") in the card's accent ink */}
            <Text
                style={{
                    fontSize: 7,
                    color: sev.ink,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontFamily: fontFamily.body,
                    fontWeight: 600,
                    marginTop: 4,
                }}
            >
                {toneLabel(tone)}
            </Text>
            {description ? (
                <View style={{ marginTop: 6 }}>
                    <Text style={{ fontSize: 7.5, color: colors.ink40, lineHeight: 1.45 }}>{description}</Text>
                </View>
            ) : null}
            {drivers.length > 0 ? (
                <View style={{ marginTop: 14 }}>
                    <Text
                        style={{
                            fontSize: 6.5,
                            color: sev.ink,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                            marginBottom: 3,
                        }}
                    >
                        Risk drivers
                    </Text>
                    {drivers.map((m, i) => {
                        const importance = getParameterImportance(m.displayName);
                        const dScore = scaleToTen(m.metricScore);
                        return (
                            <View key={i}>
                                {/* Row: label (left) + score (right) with bottom rule */}
                                <View
                                    style={{
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        paddingTop: 3,
                                        paddingBottom: 4,
                                        borderBottomWidth: 0.5,
                                        borderBottomColor: '#F0EFE9',
                                    }}
                                >
                                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', marginRight: 4 }}>
                                        <Text
                                            style={{
                                                fontSize: 8,
                                                color: colors.ink20,
                                                fontFamily: fontFamily.body,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {m.displayName}
                                        </Text>
                                        {importance ? (
                                            // Importance chip — three-part centering (skill rule 9):
                                            // lineHeight:1 on Text, alignItems/justifyContent center on View,
                                            // asymmetric padding (top 1 / bottom 2) to counter baseline drift.
                                            // Color comes from IMPORTANCE_META so C/H/M/L always carry the
                                            // same hue regardless of which pillar card they sit in.
                                            <View
                                                style={{
                                                    marginLeft: 4,
                                                    paddingHorizontal: 3,
                                                    paddingTop: 1,
                                                    paddingBottom: 2,
                                                    backgroundColor: IMPORTANCE_META[importance].bg,
                                                    borderRadius: 1.5,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: 5.5,
                                                        color: IMPORTANCE_META[importance].fg,
                                                        fontFamily: fontFamily.sansBold,
                                                        letterSpacing: 0.3,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {IMPORTANCE_META[importance].abbr}
                                                </Text>
                                            </View>
                                        ) : null}
                                    </View>
                                    <Text
                                        style={{
                                            fontSize: 8,
                                            fontFamily: fontFamily.mono,
                                            color: driverToneInk(dScore),
                                            flexShrink: 0,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {fmt1(dScore)}
                                    </Text>
                                </View>
                                {m.tooltipDescription ? (
                                    <Text style={{ fontSize: 7, color: colors.ink40, lineHeight: 1.45, marginTop: 3, marginBottom: 3 }}>
                                        {m.tooltipDescription.length > 100 ? m.tooltipDescription.slice(0, 99) + '…' : m.tooltipDescription}
                                    </Text>
                                ) : null}
                            </View>
                        );
                    })}
                </View>
            ) : null}
        </View>
    );
};

const PdfPendingSignals: React.FC<{ headers: ScoreHeader[] }> = ({ headers }) => {
    const groups = headers.map((h) => ({ name: h.displayName, pending: unscoredMetrics(h) })).filter((g) => g.pending.length > 0);
    if (groups.length === 0) return null;
    return (
        <View style={{ marginTop: space.sp3 }} minPresenceAhead={50}>
            <View style={pdf.divider} />
            <Text
                style={{
                    fontSize: typeScale.micro,
                    color: colors.ink40,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    fontFamily: fontFamily.body,
                    fontWeight: 600,
                    marginBottom: 4,
                }}
                wrap={false}
            >
                Pending signals
            </Text>
            {groups.map((g) => (
                <View key={g.name} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                    <Text style={{ fontSize: typeScale.caption, color: colors.ink40, width: 100 }}>{g.name}</Text>
                    {g.pending.map((m) => (
                        <PdfChip key={m.displayName} tone="muted">
                            {m.displayName}
                        </PdfChip>
                    ))}
                </View>
            ))}
        </View>
    );
};

// ── Legend block ────────────────────────────────────────────────
const GRADE_LEGEND: Array<{ letter: string; range: string; meaning: string }> = [
    { letter: 'A+', range: '≤ 1.5', meaning: 'Strongest credit profile' },
    { letter: 'A', range: '1.51 – 3.00', meaning: 'Strong, low concern' },
    { letter: 'B+', range: '3.01 – 4.50', meaning: 'Solid, monitor periodically' },
    { letter: 'B', range: '4.51 – 6.00', meaning: 'Cautious, deeper diligence advised' },
    { letter: 'C', range: '6.01 – 8.00', meaning: 'At-risk, material concerns' },
    { letter: 'D', range: '> 8.00', meaning: 'Severe, treat as red flag' },
];

const ZONE_LEGEND: Array<{ tone: Severity; label: string; range: string; meaning: string }> = [
    { tone: 'LOW', label: 'Low Risk', range: 'score ≤ 3.5', meaning: 'No immediate action' },
    { tone: 'MEDIUM', label: 'Medium Risk', range: '3.5 – 5.0', meaning: 'Emerging risk; monitor' },
    { tone: 'HIGH', label: 'Elevated', range: '5.0 – 7.0', meaning: 'Material concerns' },
    { tone: 'SEVERE', label: 'Severe', range: '> 7.0', meaning: 'Red flag for credit' },
];

const IMPORTANCE_LEGEND_ORDER: Array<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'> = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const PdfScoreLegend: React.FC = () => (
    // marginTop separates legend from the pillar grid above; minPresenceAhead
    // keeps at least the divider + section heading on the same page as the
    // first legend row, but allows the inner Grade / Severity table to break
    // across pages if the whole block doesn't fit. wrap=false on this outer
    // wrapper would force the entire ~250pt block onto a new page even when
    // most of the prior page is empty, which wastes a page when the pillar
    // grid is short. minPresenceAhead is the right tradeoff.
    <View style={{ marginTop: 18 }} minPresenceAhead={140}>
        <View style={pdf.divider} />
        <Text
            style={{
                fontSize: typeScale.micro,
                color: colors.ink40,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                fontFamily: fontFamily.body,
                fontWeight: 600,
                marginBottom: 10,
            }}
        >
            How to read this score
        </Text>
        <View style={{ flexDirection: 'row', gap: 16 }}>
            <View style={{ flex: 1 }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.ink00,
                        fontFamily: fontFamily.body,
                        fontWeight: 600,
                        marginBottom: 4,
                    }}
                >
                    Grade scale
                </Text>
                {GRADE_LEGEND.map((g) => (
                    <View
                        key={g.letter}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 3,
                            borderBottomWidth: 0.5,
                            borderBottomColor: colors.ruleSoft,
                        }}
                    >
                        <Text
                            style={{
                                width: 28,
                                fontSize: typeScale.caption,
                                fontFamily: fontFamily.body,
                                fontWeight: 700,
                                color: colors.ink00,
                            }}
                        >
                            {g.letter}
                        </Text>
                        <Text style={{ width: 76, fontSize: typeScale.micro, fontFamily: fontFamily.mono, color: colors.ink20 }}>
                            {g.range}
                        </Text>
                        <Text style={{ flex: 1, fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4 }}>{g.meaning}</Text>
                    </View>
                ))}
            </View>
            <View style={{ flex: 1 }}>
                <Text
                    style={{
                        fontSize: typeScale.micro,
                        color: colors.ink00,
                        fontFamily: fontFamily.body,
                        fontWeight: 600,
                        marginBottom: 4,
                    }}
                >
                    Severity zones
                </Text>
                {ZONE_LEGEND.map((z) => {
                    const sev = SCORE_TONE[z.tone];
                    return (
                        <View
                            key={z.tone}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingVertical: 3,
                                borderBottomWidth: 0.5,
                                borderBottomColor: colors.ruleSoft,
                            }}
                        >
                            <View
                                style={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: 2,
                                    backgroundColor: sev.bg,
                                    borderWidth: 0.5,
                                    borderColor: sev.border,
                                    marginRight: 6,
                                }}
                            />
                            <Text
                                style={{
                                    width: 60,
                                    fontSize: typeScale.caption,
                                    fontFamily: fontFamily.body,
                                    fontWeight: 700,
                                    color: sev.ink,
                                }}
                            >
                                {z.label}
                            </Text>
                            <Text style={{ width: 70, fontSize: typeScale.micro, fontFamily: fontFamily.mono, color: colors.ink20 }}>
                                {z.range}
                            </Text>
                            <Text style={{ flex: 1, fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4 }}>{z.meaning}</Text>
                        </View>
                    );
                })}
            </View>
        </View>
        <View style={{ marginTop: 10 }}>
            <Text
                style={{
                    fontSize: typeScale.micro,
                    color: colors.ink00,
                    fontFamily: fontFamily.body,
                    fontWeight: 600,
                    marginBottom: 2,
                }}
            >
                Importance tags
            </Text>
            <Text style={{ fontSize: typeScale.micro, color: colors.ink40, marginBottom: 6, lineHeight: 1.45 }}>
                The small letter next to each driver inside a pillar card shows how heavily that parameter weighs in the composite score — a
                Critical parameter counts 4× a Low one.
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
                {IMPORTANCE_LEGEND_ORDER.map((k) => {
                    const meta = IMPORTANCE_META[k];
                    return (
                        <View key={k} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <View
                                style={{
                                    paddingHorizontal: 3,
                                    paddingTop: 1,
                                    paddingBottom: 2,
                                    backgroundColor: meta.bg,
                                    borderRadius: 1.5,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 5.5,
                                        color: meta.fg,
                                        fontFamily: fontFamily.sansBold,
                                        letterSpacing: 0.3,
                                        lineHeight: 1,
                                    }}
                                >
                                    {meta.abbr}
                                </Text>
                            </View>
                            <Text style={{ fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1 }}>
                                {meta.label} ({meta.weight}%)
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
        <Text style={{ fontSize: typeScale.micro, color: colors.ink40, marginTop: 8, lineHeight: 1.5 }}>
            The composite score is the weighted average across pillars, then bucketed into the grade letter and severity zone above.
        </Text>
        <View style={pdf.divider} />
    </View>
);

const PdfScore: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const s = data.score;
    if (!s) return null;
    const scaledCompany = scaleToTen(s.companyScore);
    const grade = gradeFromScore(scaledCompany);
    const headlineTone = toneForScore(scaledCompany);
    const headlineSev = SCORE_TONE[headlineTone];
    return (
        <PdfSection kicker="AICA grade" title="AICA Risk Score" index={index} breakBefore={breakBefore}>
            {/* ── Composite banner ──
                Per Figma 131:85400 — soft beige surface (no purple), dark ink digits,
                "COMPOSITE SCORE | GRADE | tone copy" rendered on a single horizontal
                axis. Lighter, more editorial than the previous purple banner. */}
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 28,
                    paddingHorizontal: 14,
                    paddingTop: 14,
                    paddingBottom: 14,
                    borderRadius: 4,
                    backgroundColor: colors.surface,
                }}
                wrap={false}
            >
                {/* Composite score column */}
                <View style={{ flexShrink: 0, width: 110 }}>
                    <Text
                        style={{
                            fontSize: 6.5,
                            color: colors.ink40,
                            letterSpacing: 0.7,
                            textTransform: 'uppercase',
                            fontFamily: fontFamily.body,
                            fontWeight: 600,
                        }}
                    >
                        Composite Risk Score
                    </Text>
                    {/* Skill rule 6: lineHeight 1 on the large display digits so the
                        glyph descenders don't push into the row below. "/ 10" suffix
                        aligns to baseline via flex-end. */}
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginTop: 6 }}>
                        <Text
                            style={{
                                fontSize: 38,
                                color: colors.ink00,
                                fontFamily: fontFamily.mono,
                                fontWeight: 600,
                                lineHeight: 1,
                                letterSpacing: -0.5,
                            }}
                        >
                            {fmt1(scaledCompany)}
                        </Text>
                        <Text style={{ fontSize: 9, color: colors.ink40, marginLeft: 4, fontFamily: fontFamily.mono, marginBottom: 2 }}>
                            / 10
                        </Text>
                    </View>
                </View>
                {/* Grade + tone copy column */}
                <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 18 }}>
                        <View style={{ flexShrink: 0, width: 56 }}>
                            <Text
                                style={{
                                    fontSize: 6.5,
                                    color: colors.ink40,
                                    letterSpacing: 0.7,
                                    textTransform: 'uppercase',
                                    fontFamily: fontFamily.body,
                                    fontWeight: 600,
                                }}
                            >
                                Grade
                            </Text>
                            <Text
                                style={{
                                    fontSize: 28,
                                    fontFamily: fontFamily.display,
                                    color: colors.ink00,
                                    lineHeight: 1,
                                    marginTop: 4,
                                }}
                            >
                                {grade}
                            </Text>
                        </View>
                        <View style={{ flex: 1 }}>
                            {/* Tone kicker — inline "MEDIUM RISK · short copy" on one
                                wrapping line. */}
                            <Text style={{ fontSize: 7.5, lineHeight: 1.5 }}>
                                <Text
                                    style={{
                                        color: headlineSev.ink,
                                        fontFamily: fontFamily.body,
                                        fontWeight: 700,
                                        letterSpacing: 0.6,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {toneLabel(headlineTone)} ·{' '}
                                </Text>
                                <Text style={{ color: colors.ink40 }}>{TONE_BANNER_COPY[headlineTone]}</Text>
                            </Text>
                            <Text style={{ fontSize: 7.5, color: colors.ink40, lineHeight: 1.5, marginTop: 8 }}>
                                {COMPOSITE_DESCRIPTION}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
            {/* ── Pillar grid ──
                Figma shows 3-up; we keep 2-up because at A4 body width 3-up cards
                collapse below 175pt — too tight for description + 3 weakest-driver
                rows. Pillar count also varies (4–7) so 2-up packs more cleanly.
                Skill rule 2: explicit row pairs, never flexWrap. */}
            <View style={{ flexDirection: 'column', gap: 10, marginTop: 18 }}>
                {(() => {
                    const pairs: Array<[(typeof s.headers)[number] | undefined, (typeof s.headers)[number] | undefined]> = [];
                    for (let i = 0; i < s.headers.length; i += 2) pairs.push([s.headers[i], s.headers[i + 1]]);
                    return pairs.map((row, ri) => (
                        // Skill rule 23: when one card grows taller than the other,
                        // the shorter one stretches. `justifyContent: space-between`
                        // on the card body would distribute rows; here we let each
                        // card lay out naturally — content density is similar enough
                        // that stretching looks fine.
                        // `alignItems: 'flex-start'` so a shorter card (fewer
                        // drivers / no description) doesn't stretch vertically
                        // to match its taller sibling — that was leaving dead
                        // empty space below the content in row 2 (Management +
                        // Governance). Cards now size to their own content;
                        // rows still align at the top.
                        <View key={ri} style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }} wrap={false}>
                            {row.map((h, ci) =>
                                h ? (
                                    <View key={ci} style={{ flex: 1 }}>
                                        <PdfPillarCard header={h} />
                                    </View>
                                ) : (
                                    <View key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </View>
                    ));
                })()}
            </View>
            <PdfScoreLegend />
        </PdfSection>
    );
};

// ---------------- Screen ----------------

const ScreenPillarCard: React.FC<{ header: ScoreHeader }> = ({ header }) => {
    const scaledHeader = scaleToTen(header.headerScore);
    const tone = toneForScore(scaledHeader);
    const sev = SCORE_TONE[tone];
    const drivers = weakestDrivers(header);
    const description = pillarDescription(header);
    return (
        <div
            style={{
                flex: 1,
                padding: 14,
                borderRadius: 6,
                background: sev.bg,
                border: `1px solid ${sev.border}`,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: colors.ink00, lineHeight: 1.3 }}>
                    {header.displayName}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'flex-end', flexShrink: 0 }}>
                    <span
                        style={{
                            fontSize: '1.3rem',
                            fontFamily: "'Geist Mono', monospace",
                            fontWeight: 600,
                            color: colors.ink00,
                            lineHeight: 1,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {fmt1(scaledHeader)}
                    </span>
                    <span
                        style={{
                            fontSize: '0.7rem',
                            color: colors.ink40,
                            marginLeft: 4,
                            fontFamily: "'Geist Mono', monospace",
                            lineHeight: 1,
                        }}
                    >
                        / 10
                    </span>
                </div>
            </div>
            <div
                style={{
                    fontSize: '0.65rem',
                    color: sev.ink,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    marginTop: 6,
                }}
            >
                {toneLabel(tone)}
            </div>
            {description ? (
                <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 8, lineHeight: 1.5 }}>{description}</div>
            ) : null}
            {drivers.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                    <div
                        style={{
                            fontSize: '0.62rem',
                            color: sev.ink,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            marginBottom: 4,
                        }}
                    >
                        Risk drivers
                    </div>
                    {drivers.map((m, i) => {
                        const importance = getParameterImportance(m.displayName);
                        const dScore = scaleToTen(m.metricScore);
                        return (
                            <div key={i}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '4px 0 5px',
                                        borderBottom: '1px solid #F0EFE9',
                                    }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: '0.78rem', color: colors.ink20, lineHeight: 1 }}>{m.displayName}</span>
                                        {importance ? (
                                            <span
                                                title={`Importance: ${IMPORTANCE_META[importance].label} (${IMPORTANCE_META[importance].weight}%)`}
                                                style={{
                                                    background: IMPORTANCE_META[importance].bg,
                                                    color: IMPORTANCE_META[importance].fg,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.3,
                                                    padding: '1px 4px',
                                                    borderRadius: 2,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {IMPORTANCE_META[importance].abbr}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: '0.78rem',
                                            fontFamily: "'Geist Mono', monospace",
                                            color: driverToneInk(dScore),
                                            lineHeight: 1,
                                        }}
                                    >
                                        {fmt1(dScore)}
                                    </span>
                                </div>
                                {m.tooltipDescription ? (
                                    <div
                                        style={{ fontSize: '0.7rem', color: colors.ink40, lineHeight: 1.5, marginTop: 4, marginBottom: 4 }}
                                    >
                                        {m.tooltipDescription}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

const ScreenPendingSignals: React.FC<{ headers: ScoreHeader[] }> = ({ headers }) => {
    const groups = headers.map((h) => ({ name: h.displayName, pending: unscoredMetrics(h) })).filter((g) => g.pending.length > 0);
    if (groups.length === 0) return null;
    return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
            <div
                style={{
                    fontSize: '0.7rem',
                    color: colors.ink40,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: 6,
                }}
            >
                Pending signals
            </div>
            {groups.map((g) => (
                <div key={g.name} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: colors.ink40, width: 130 }}>{g.name}</span>
                    {g.pending.map((m) => (
                        <ScreenChip key={m.displayName} tone="muted">
                            {m.displayName}
                        </ScreenChip>
                    ))}
                </div>
            ))}
        </div>
    );
};

const ScreenScoreLegend: React.FC = () => (
    <div style={{ marginTop: 20, paddingTop: 12, borderTop: `1px solid ${colors.rule}` }}>
        <div
            style={{
                fontSize: '0.7rem',
                color: colors.ink40,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: 12,
            }}
        >
            How to read this score
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Grade scale</div>
                {GRADE_LEGEND.map((g) => (
                    <div
                        key={g.letter}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 0',
                            borderBottom: `0.5px solid ${colors.ruleSoft}`,
                            gap: 8,
                        }}
                    >
                        <span style={{ width: 32, fontSize: '0.85rem', fontWeight: 700, color: colors.ink00 }}>{g.letter}</span>
                        <span
                            style={{
                                width: 100,
                                fontSize: '0.7rem',
                                fontFamily: "'Geist Mono', monospace",
                                color: colors.ink20,
                            }}
                        >
                            {g.range}
                        </span>
                        <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{g.meaning}</span>
                    </div>
                ))}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 6 }}>Severity zones</div>
                {ZONE_LEGEND.map((z) => {
                    const sev = SCORE_TONE[z.tone];
                    return (
                        <div
                            key={z.tone}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 0',
                                borderBottom: `0.5px solid ${colors.ruleSoft}`,
                                gap: 8,
                            }}
                        >
                            <span
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 3,
                                    background: sev.bg,
                                    border: `1px solid ${sev.border}`,
                                    display: 'inline-block',
                                }}
                            />
                            <span style={{ width: 90, fontSize: '0.78rem', fontWeight: 700, color: sev.ink }}>{z.label}</span>
                            <span
                                style={{
                                    width: 90,
                                    fontSize: '0.7rem',
                                    fontFamily: "'Geist Mono', monospace",
                                    color: colors.ink20,
                                }}
                            >
                                {z.range}
                            </span>
                            <span style={{ flex: 1, fontSize: '0.72rem', color: colors.ink40, lineHeight: 1.45 }}>{z.meaning}</span>
                        </div>
                    );
                })}
            </div>
        </div>
        <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.75rem', color: colors.ink00, fontWeight: 600, marginBottom: 2 }}>Importance tags</div>
            <div style={{ fontSize: '0.72rem', color: colors.ink40, marginBottom: 8, lineHeight: 1.5 }}>
                The small letter next to each driver inside a pillar card shows how heavily that parameter weighs in the composite score — a
                Critical parameter counts 4× a Low one.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                {IMPORTANCE_LEGEND_ORDER.map((k) => {
                    const meta = IMPORTANCE_META[k];
                    return (
                        <div key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span
                                style={{
                                    background: meta.bg,
                                    color: meta.fg,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    padding: '1px 4px',
                                    borderRadius: 2,
                                    lineHeight: 1,
                                }}
                            >
                                {meta.abbr}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: colors.ink40, lineHeight: 1 }}>
                                {meta.label} ({meta.weight}%)
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
        <div style={{ fontSize: '0.72rem', color: colors.ink40, marginTop: 10, lineHeight: 1.55 }}>
            The composite score is the weighted average across pillars, then bucketed into the grade letter and severity zone above.
        </div>
        <div style={{ height: 1, background: colors.rule, marginTop: 14 }} />
    </div>
);

const ScreenScore: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const s = data.score;
    if (!s) return null;
    const scaledCompany = scaleToTen(s.companyScore);
    const grade = gradeFromScore(scaledCompany);
    const headlineTone = toneForScore(scaledCompany);
    const headlineSev = SCORE_TONE[headlineTone];
    return (
        <ScreenSection kicker="AICA grade" title="AICA Risk Score" index={index}>
            {/* Composite banner — mirrors PdfScore. Soft beige surface, no purple. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 36,
                    padding: '18px 20px',
                    borderRadius: 6,
                    background: colors.surface,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ flexShrink: 0, minWidth: 140 }}>
                    <div
                        style={{
                            fontSize: '0.66rem',
                            color: colors.ink40,
                            fontWeight: 600,
                            letterSpacing: '0.09em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Composite Risk Score
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', marginTop: 8 }}>
                        <div
                            style={{
                                fontSize: '3.2rem',
                                color: colors.ink00,
                                fontFamily: "'Geist Mono', monospace",
                                fontWeight: 600,
                                lineHeight: 1,
                                letterSpacing: '-0.025em',
                            }}
                        >
                            {fmt1(scaledCompany)}
                        </div>
                        <div
                            style={{
                                fontSize: '0.85rem',
                                color: colors.ink40,
                                marginLeft: 6,
                                fontFamily: "'Geist Mono', monospace",
                                marginBottom: 4,
                            }}
                        >
                            / 10
                        </div>
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 260, display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, minWidth: 64 }}>
                        <div
                            style={{
                                fontSize: '0.66rem',
                                color: colors.ink40,
                                letterSpacing: '0.09em',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                            }}
                        >
                            Grade
                        </div>
                        <div
                            style={{
                                fontSize: '2.4rem',
                                fontFamily: "'Fraunces', serif",
                                color: colors.ink00,
                                lineHeight: 1,
                                marginTop: 6,
                            }}
                        >
                            {grade}
                        </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: '0.75rem', lineHeight: 1.55 }}>
                            <span
                                style={{
                                    color: headlineSev.ink,
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {toneLabel(headlineTone)} ·{' '}
                            </span>
                            <span style={{ color: colors.ink40 }}>{TONE_BANNER_COPY[headlineTone]}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: colors.ink40, lineHeight: 1.55, marginTop: 10 }}>
                            {COMPOSITE_DESCRIPTION}
                        </div>
                    </div>
                </div>
            </div>
            {/* Pillar grid — 2-up rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
                {(() => {
                    const pairs: Array<[(typeof s.headers)[number] | undefined, (typeof s.headers)[number] | undefined]> = [];
                    for (let i = 0; i < s.headers.length; i += 2) pairs.push([s.headers[i], s.headers[i + 1]]);
                    return pairs.map((row, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 10 }}>
                            {row.map((h, ci) =>
                                h ? (
                                    <div key={ci} style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                        <ScreenPillarCard header={h} />
                                    </div>
                                ) : (
                                    <div key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </div>
                    ));
                })()}
            </div>
            <ScreenScoreLegend />
        </ScreenSection>
    );
};

export const scoreModule: ModuleDefinition = {
    meta: {
        id: 'score',
        title: 'AICA Risk Score',
        description: 'Composite grade with per-pillar risk cards, weakest drivers and pending signals.',
        dataSource: 'Assessee Score API',
    },
    hasData: (d) => {
        if (!d.score || !Number.isFinite(d.score.companyScore)) return false;
        return d.score.companyScore > 0 || d.score.headers.some((h) => Number.isFinite(h.headerScore));
    },
    Pdf: PdfScore,
    Screen: ScreenScore,
};

export { toneForScore };
